import { useTranslation } from 'react-i18next'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { ServiceModuleCard } from './ServiceModuleCard'
import { SortableGrid } from '@/components/common/sortable/SortableGrid'
import { DragHandle } from '@/components/common/sortable/DragHandle'
import type { ServiceModuleModel } from '@/api/model'

const GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'

interface Props {
  /** Modules of the currently displayed level (root or folder content) — already filtered. */
  modules: ServiceModuleModel[]
  isLoading: boolean
  isError: boolean
  /** true = we are inside a folder (only affects the empty text). */
  inFolder?: boolean
  /** true = a search is active (only affects the empty text). */
  isFiltered?: boolean
  onEdit: (module: ServiceModuleModel) => void
  /** Card click = open the module (type-dependent view; Folder → navigate into it). */
  onOpen: (module: ServiceModuleModel) => void
  onMove: (module: ServiceModuleModel) => void
  /** When set: level is sortable via D&D; receives the new id order. */
  onReorder?: (orderedIds: number[]) => void
}

/**
 * Tile grid of the current level — display only. Filtering by folder and the
 * loading are handled by the page (composition root).
 */
export function ServiceModuleGrid({ modules, isLoading, isError, inFolder, isFiltered, onEdit, onOpen, onReorder, onMove }: Props) {
  const { t } = useTranslation('service-modules')

  const emptyMessage = isFiltered
    ? t('grid.noResults')
    : inFolder ? t('grid.emptyFolder') : t('grid.emptyRoot')

  return (
    <LoaderGeneral
      isLoading={isLoading}
      isError={isError}
      isEmpty={modules.length === 0}
      emptyMessage={emptyMessage}
    >
      {/* Sortable only when the page allows it (edit permission, no active search). */}
      {onReorder ? (
        <SortableGrid
          items={modules}
          onReorder={onReorder}
          className={GRID_CLASS}
          renderItem={(module, handle) => (
            <ServiceModuleCard
              module={module}
              onEdit={onEdit}
              onOpen={onOpen}
              onMove={onMove}
              dragHandle={<DragHandle {...handle} />}
            />
          )}
        />
      ) : (
        <div className={GRID_CLASS}>
          {modules.map((module) => (
            <ServiceModuleCard key={module.id} module={module} onEdit={onEdit} onOpen={onOpen} onMove={onMove} />
          ))}
        </div>
      )}
    </LoaderGeneral>
  )
}
